import { useEffect, useState } from "react";
import { Controller, FieldError, useFormContext, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import ConfirmationModal from "components/ConfirmationModal";
import DataTable from "components/DataTable";
import DatePicker from "components/DatePicker";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Input from "components/Input";
import PhoneNumber from "components/PhoneNumber";
import Select from "components/Select";
import {
  DUPLICATED_INVOICE_TABLE_HEADER_LIST,
  FR_CONTRACT_TYPE,
  PERCENTAGE_LIST
} from "constants/haulpay-broker";
import { CARRIER_PAYMENT_SPEED } from "constants/haulpay-carrier";
import { routes } from "constants/routes";
import { EQUIPMENT_TYPES } from "constants/signup";
import DuplicatedInvoiceForMobile from "features/main/HaulPay/Broker/modules/CreateFundingRequest/DuplicatedInvoiceForMobile";
import useDeviceType from "hooks/useDeviceType";
import { useUserFeature } from "hooks/useUserFeature";
import { IOption } from "models/Form";
import { ICompany, IInvoiceData, IPaymentProfile } from "models/HaulPay";
import { searchPoNumberAPI } from "services/api/haulpay";
import CarrierSelector from "../../CarrierSelector";
import { IEditFundingRequest } from "../Manage";
import PaymentSpeedInput from "components/PaymentSpeedInput";
import styles from "./style.module.scss";
import DebtorSearch from "components/DebtorSearchInput";
import { FUNDING_REQUEST_CONTRACT_TYPES } from "helpers/constantTypes";
import DollarInput from "components/DollarInput";

interface ISharedValue {
  isGlobalDisabled: boolean;
  fundingRequestValue: string;
  selectedPaymentProfile?: IPaymentProfile;
  setSelectedPaymentProfile: (item: IPaymentProfile) => void;
  setIsShowSearch?: (show: boolean) => void;
  companyValue?: ICompany;
  isFactoringCompany?: boolean;
  carrierCanEdit: boolean;
}

interface IVisibleLabel {
  label: string;
  isHide: boolean;
}

const VisibleLabel = ({ label, isHide }: Readonly<IVisibleLabel>) => {
  return !isHide ? (
    <span className={clsx("px-1 text-xs text-placeholder mt-1")}>{label}</span>
  ) : null;
};

export default function StepTwo({
  isGlobalDisabled,
  fundingRequestValue,
  selectedPaymentProfile,
  setSelectedPaymentProfile,
  isFactoringCompany,
  companyValue,
  carrierCanEdit
}: Readonly<ISharedValue>) {
  const navigate = useNavigate();
  const { isCarrier } = useUserFeature();
  const { isDesktop } = useDeviceType();
  const [date, setDate] = useState("");
  const {
    register,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
    control
  } = useFormContext<Partial<IEditFundingRequest>>();

  const contractType = useWatch({ control: control, name: "fundingRequestType" });
  const isNonFactoredFR = [
    FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD,
    FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT
  ].includes(contractType?.value ?? "");
  const [poNumber, setPoNumber] = useState<string>();
  const [poNumberError, setPoNumberError] = useState<FieldError>();
  const [duplicatedInvoice, setDuplicatedInvoice] = useState<IInvoiceData[]>([]);
  const fundingRequestTypeValue = useWatch({
    control: control,
    name: "fundingRequestType.value"
  });

  const [isDuplicatedInvoiceModalOpen, toggleDuplicatedInvoiceModal] = useToggle();

  const goToInvoice = (invoice: IInvoiceData) => {
    navigate(routes.HAULPAY_BROKER_MANAGE_PAY, {
      state: {
        debtor: invoice.debtor?.id,
        debtorName: invoice.debtor?.dba
      }
    });
  };

  const setPaymentProfile = (paymentProfile: IPaymentProfile) => {
    setSelectedPaymentProfile(paymentProfile);
    setValue("paymentProfile", paymentProfile.company_name);
  };
  useEffect(() => {
    if (selectedPaymentProfile) {
      setValue("carrierEmail", selectedPaymentProfile?.owner_email ?? "");
      setValue("carrierPhone", selectedPaymentProfile?.owner_phone_number_cell ?? "");
      setValue("carrierMc", selectedPaymentProfile?.company_mc ?? "");
      if (selectedPaymentProfile.factoring_company?.name) {
        setValue(
          "carrierPaymentMethod",
          `${selectedPaymentProfile.factoring_company.name} (**** ${selectedPaymentProfile.factoring_company.accountNumber})`
        );
        setValue("paymentProfileDisplay", selectedPaymentProfile.factoring_company.method);
        setValue("brokersQRFee", PERCENTAGE_LIST[2]);
      } else {
        if ((selectedPaymentProfile.accounts ?? []).length) {
          setValue(
            "carrierPaymentMethod",
            `${selectedPaymentProfile.accounts[0]?.method} (**** ${selectedPaymentProfile.accounts[0]?.accountNumberRedacted})`
          );
          setValue("paymentProfileDisplay", selectedPaymentProfile.accounts[0]?.method);
        } else {
          setValue("paymentProfileDisplay", "ACH");
          setValue("carrierPaymentMethod", "ACH");
        }
        setValue(
          "brokersQRFee",
          PERCENTAGE_LIST.find(
            ({ amount }) => amount === Number(selectedPaymentProfile.factoring_fee_split_rate)
          )
        );
      }
      setValue(
        "carrierPaymentSpeed",
        CARRIER_PAYMENT_SPEED.find((item) => item.value === selectedPaymentProfile.payout_days)
      );
    } else {
      setValue("carrierEmail", "");
      setValue("carrierPhone", "");
      setValue("carrierMc", "");
    }
  }, [selectedPaymentProfile]);

  useEffect(() => {
    const searchPoNumber = async () => {
      if (poNumber) {
        try {
          const response = await searchPoNumberAPI(poNumber);
          if (response?.data?.length) {
            setPoNumberError({
              type: "custom",
              message: "The PO # has been used on another invoice Click Here to view"
            });
            setDuplicatedInvoice(response.data);
          } else {
            setPoNumberError(undefined);
          }
        } catch {
          /* Empty */
        }
      }
    };

    searchPoNumber();
  }, [poNumber]);
  return (
    <div className={styles.fundingRequestStepTwo}>
      <div className={styles.container}>
        {!isCarrier ? (
          <>
            <div className={styles.sectionTwo}>
              <Input label="Carrier MC" name="carrierMc" register={register} disabled />
            </div>
            <div className={styles.sectionTwo}>
              <Input label="Carrier Email" name="carrierEmail" register={register} disabled />
            </div>
            <div className={styles.section}>
              <DollarInput
                label="&nbsp;&nbsp;Broker Pay"
                name="brokerPay"
                disabled={!isCarrier || isGlobalDisabled}
              />
              <DollarInput
                label="&nbsp;&nbsp;Your Fee"
                name="yourFee"
                disabled={!isCarrier || isGlobalDisabled}
              />
            </div>
            <div className={styles.sectionTwo}>
              <Input
                label="&nbsp;PO #"
                name="PONumber"
                register={register}
                disabled={isGlobalDisabled}
                error={poNumberError}
                isHideError
                onBlur={(event) => {
                  setPoNumber(event.target.value);
                  setValue("PONumber", event.target.value);
                }}
              />
              <VisibleLabel label="(Visible to Debtor)" isHide={isCarrier} />

              {poNumberError ? (
                <div className="self-end text-error mobile:text-sm mobile:mt-1">
                  <span>The PO # has been used on another invoice Click Here </span>
                  <button
                    type="button"
                    className="text-info"
                    onClick={toggleDuplicatedInvoiceModal}>
                    Click Here
                  </button>
                  <span> to view</span>
                </div>
              ) : null}
            </div>
            <div className={styles.sectionTwo}>
              <span className="text-sm text-tertiary">&nbsp;*Carrier</span>
              <CarrierSelector
                name="paymentProfile"
                register={register}
                errors={errors}
                getValues={getValues}
                setPaymentProfile={setPaymentProfile}
                isSearchCompanyName
                useGroupIC
              />
            </div>
            <div className={styles.sectionTwo}>
              <Input
                label="&nbsp;Carrier Pro #"
                name="carrierPro"
                register={register}
                disabled={isGlobalDisabled}
              />
              <VisibleLabel label="(Visible to Carrier)" isHide={isCarrier} />
            </div>
            {fundingRequestTypeValue !== "non-factored-bill-out-only" &&
              fundingRequestTypeValue !== "non-factored-pay-carrier" && (
                <>
                  <Controller
                    name="brokersQRFee"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        label={
                          <p className="pl-1">
                            <span>Broker&apos;s % of QP Fee </span>
                            <span className="text-success">
                              (Carrier Rate will change depending on the portion % you absorb)
                            </span>
                          </p>
                        }
                        options={PERCENTAGE_LIST}
                        value={value}
                        disabled={isFactoringCompany || isNonFactoredFR || isGlobalDisabled}
                        onChange={onChange}
                        {...(!isDesktop && { direction: "up" })}
                      />
                    )}
                  />
                </>
              )}
            <div className={styles.section}>
              <Input
                label="&nbsp;&nbsp;Load Length"
                name="loadLength"
                register={register}
                disabled={!carrierCanEdit || isGlobalDisabled}
                error={errors.loadLength}
              />
              <Controller
                name="equipmentType"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    label="&nbsp;&nbsp;Equipment Type"
                    options={EQUIPMENT_TYPES}
                    value={value}
                    onChange={onChange}
                    className="desktop:w-5/12"
                    disabled={!carrierCanEdit || !isCarrier || isGlobalDisabled}
                  />
                )}
              />
            </div>
            <div className={styles.sectionTwo}>
              <Input
                label="&nbsp;&nbsp;Carrier Invoice Details"
                name="carrierInvoiceDetails"
                register={register}
                disabled={isGlobalDisabled}
              />
              <VisibleLabel label="(Visible to Carrier)" isHide={isCarrier} />
            </div>
            {fundingRequestTypeValue !== "non-factored-bill-out-only" && (
              <DollarInput
                label="&nbsp;Carrier Invoice Amount"
                name="carrierInvoiceAmount"
                error={errors.carrierInvoiceAmount}
              />
            )}
            <div className={styles.sectionTwo}>
              <Input
                label="&nbsp;&nbsp;Client Invoice #"
                name="clientInvoiceNumber"
                register={register}
                disabled={isGlobalDisabled}
              />
              <VisibleLabel label="(Visible to Shipper and Carrier)" isHide={isCarrier} />
            </div>
            <Controller
              name="finalDelivery"
              control={control}
              render={({ field: { value, onChange } }) => (
                <GoogleAutoComplete
                  label="&nbsp;&nbsp;Final Delivery"
                  value={value}
                  onChange={onChange}
                  disabled={isGlobalDisabled || isCarrier}
                  error={errors.finalDelivery?.country as any}
                />
              )}
            />
            <Input
              label="&nbsp;&nbsp;Carrier Scheduled date"
              name="expectedProcessingAt"
              register={register}
              disabled
            />
            {isDesktop ? (
              <div className={styles.sectionTwo}>
                <PaymentSpeedInput
                  control={control}
                  name="carrierPaymentSpeed"
                  customOnChange={(paymentSpeed) => {
                    !selectedPaymentProfile?.factoring_company?.name
                      ? setValue(
                          "brokersQRFee",
                          PERCENTAGE_LIST.find(
                            (obj) => obj.value === String(paymentSpeed.splitFee)
                          ) as IOption
                        )
                      : setValue("brokersQRFee", PERCENTAGE_LIST[0]);
                  }}
                  inputProps={{
                    disabled: isGlobalDisabled || isNonFactoredFR,
                    direction: "up"
                  }}
                />
                <span className={clsx(styles.visibleLabel, "opacity-0")}>(Visible to Carrier)</span>
              </div>
            ) : null}
            <div className="flex flex-col gap-0.5">
              <Controller
                name="firstPickUp"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <GoogleAutoComplete
                    label="&nbsp;&nbsp;First Pick Up"
                    value={value}
                    onChange={onChange}
                    disabled={isGlobalDisabled || isCarrier}
                    error={errors.firstPickUp?.country as FieldError}
                  />
                )}
              />
              {isDesktop ? <span className="text-xs opacity-0">(Visible to Shipper)</span> : null}
            </div>
            {fundingRequestValue !== "self-financed-no-invoicing" ? (
              <div className={styles.sectionTwo}>
                <Input
                  label="&nbsp;Shipper/Debtor Invoice Details"
                  name="debtorInvoiceDetails"
                  register={register}
                  disabled={isGlobalDisabled}
                />
                <VisibleLabel label="(Visible to Shipper)" isHide={isCarrier} />
              </div>
            ) : null}
          </>
        ) : null}
        {isCarrier && (
          <>
            <Controller
              name="finalDelivery"
              control={control}
              render={({ field: { value, onChange } }) => (
                <GoogleAutoComplete
                  label="&nbsp;&nbsp;Final Delivery"
                  value={value}
                  onChange={onChange}
                  disabled={isGlobalDisabled || isCarrier}
                  error={errors.finalDelivery?.country as any}
                />
              )}
            />
            <Controller
              name="firstPickUp"
              control={control}
              render={({ field: { value, onChange } }) => (
                <GoogleAutoComplete
                  label="&nbsp;&nbsp;First Pick Up"
                  value={value}
                  onChange={onChange}
                  disabled={isGlobalDisabled || isCarrier}
                  error={errors.firstPickUp?.country as FieldError}
                />
              )}
            />
            <div className={styles.section}>
              <Input
                label="&nbsp;&nbsp;Load Length"
                name="loadLength"
                register={register}
                disabled={!carrierCanEdit || isGlobalDisabled}
                error={errors.loadLength}
              />
              <Controller
                name="equipmentType"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    label="&nbsp;&nbsp;Equipment Type"
                    options={EQUIPMENT_TYPES}
                    value={value}
                    onChange={onChange}
                    className="desktop:w-5/12"
                    disabled={!carrierCanEdit || !isCarrier || isGlobalDisabled}
                  />
                )}
              />
            </div>
            <Input
              label="&nbsp;&nbsp;Invoice Date"
              name="brokerFundedAt"
              register={register}
              disabled
            />
            <div className="flex flex-col gap-0.5">
              <DebtorSearch
                name="billToCompany"
                value={companyValue?.dba || ""}
                onSelect={(company: ICompany) => {
                  const strAddress = [
                    company.address?.street_one,
                    company.address?.street_two,
                    company.address?.city,
                    company.address?.state,
                    company.address?.zip,
                    company.address?.country
                  ]
                    .filter((item) => !!item && item !== undefined)
                    .join(", ");
                  setValue("billToCompanyAddress", {
                    ...company.address,
                    formattedAddress: strAddress
                  } as any);
                  setValue("billToCompanyPhone", company.phone_number);
                  setValue("billToCompany", company);
                  clearErrors("billToCompany");
                }}
                label="&nbsp;&nbsp;Bill To"
                error={errors?.billToCompany as any}
                disabled={!carrierCanEdit || fundingRequestValue === FR_CONTRACT_TYPE[3].value}
                isConnectedDebtor
              />
              <VisibleLabel label="(Visible to Shipper and Carrier)" isHide={isCarrier} />
            </div>
            <div className="flex flex-col gap-0.5">
              <Controller
                name="billToCompanyAddress"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <GoogleAutoComplete
                    label="&nbsp;&nbsp;Bill To Company Address"
                    value={value}
                    onChange={onChange}
                    error={errors.billToCompanyAddress as any}
                    disabled
                  />
                )}
              />
              <VisibleLabel label="(Visible to Carrier)" isHide={isCarrier} />
            </div>
            <div className="flex flex-col gap-0.5">
              <Controller
                name="billToCompanyPhone"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <PhoneNumber
                    value={value}
                    label="&nbsp;&nbsp;Bill To Company Phone"
                    onChange={onChange}
                    error={errors.billToCompanyPhone}
                    className="flex-1"
                    wrapperClass="flex-1"
                    disabled
                  />
                )}
              />
              <VisibleLabel label="(Visible to Shipper)" isHide={isCarrier} />
            </div>
            <Controller
              name="createdAt"
              control={control}
              render={({ field: { value = date, onChange } }) => (
                <DatePicker
                  label="&nbsp;&nbsp;Created At"
                  placeholder="Select Date & Time"
                  value={value}
                  onChange={(value) => {
                    setDate(value);
                    onChange(value);
                  }}
                  disabled
                />
              )}
            />
          </>
        )}
      </div>
      <ConfirmationModal
        title="Invoices with matching PO#"
        isConfirmModalOpen={isDuplicatedInvoiceModalOpen}
        toggleConfirmModal={toggleDuplicatedInvoiceModal}
        isHideHeaderIcon
        isHideCancelButton
        isHideOKButton
        content={
          <div className="mobile:flex mobile:flex-col mobile:gap-3">
            {isDesktop ? (
              <DataTable
                headers={DUPLICATED_INVOICE_TABLE_HEADER_LIST}
                data={duplicatedInvoice.map((invoice) => ({
                  ...invoice,
                  linkToInvoice: {
                    action: () => goToInvoice(invoice)
                  }
                }))}
              />
            ) : (
              duplicatedInvoice.map((invoice) => (
                <DuplicatedInvoiceForMobile
                  key={invoice.id}
                  data={invoice}
                  goToInvoice={goToInvoice}
                />
              ))
            )}
          </div>
        }
      />
    </div>
  );
}
